import React from "react";
import { Invoice, InvoiceTemplate as TemplateId } from "@/types/invoice";

interface BusinessInfo {
  name: string;
  logo?: string;
  address?: string;
  email?: string;
  phone?: string;
  website?: string;
  currency?: string;
}

interface InvoiceTemplateProps {
  invoice: Invoice;
  business: BusinessInfo;
}

function fmt(n: number, currency = "NGN") {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(n);
}

function lineTotal(item: Invoice["items"][number]) {
  const base = item.unitPrice * item.quantity;
  return base - base * ((item.discount || 0) / 100);
}

function StatusBadge({ status }: { status: Invoice["status"] }) {
  const colors: Record<string, string> = {
    draft: "#6b7280",
    sent: "#3b82f6",
    partial: "#f59e0b",
    paid: "#10b981",
    overdue: "#ef4444",
    cancelled: "#9ca3af",
  };
  return (
    <span
      style={{
        background: colors[status] || "#6b7280",
        color: "#fff",
        padding: "3px 10px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {status}
    </span>
  );
}

function ItemsTable({ invoice, currency }: { invoice: Invoice; currency: string }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}>
      <thead>
        <tr style={{ background: "#f3f4f6", fontSize: 12, fontWeight: 600, color: "#374151" }}>
          <th style={{ textAlign: "left", padding: "8px 10px" }}>Item</th>
          <th style={{ textAlign: "right", padding: "8px 10px" }}>Qty</th>
          <th style={{ textAlign: "right", padding: "8px 10px" }}>Unit Price</th>
          <th style={{ textAlign: "right", padding: "8px 10px" }}>Disc.</th>
          <th style={{ textAlign: "right", padding: "8px 10px" }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {invoice.items.map((item, i) => (
          <tr key={item._id || i} style={{ borderBottom: "1px solid #e5e7eb", fontSize: 13 }}>
            <td style={{ padding: "8px 10px" }}>
              <div style={{ fontWeight: 500 }}>{item.itemName}</div>
              {item.description && (
                <div style={{ color: "#6b7280", fontSize: 11 }}>{item.description}</div>
              )}
            </td>
            <td style={{ textAlign: "right", padding: "8px 10px" }}>{item.quantity}</td>
            <td style={{ textAlign: "right", padding: "8px 10px" }}>{fmt(item.unitPrice, currency)}</td>
            <td style={{ textAlign: "right", padding: "8px 10px" }}>
              {item.discount ? `${item.discount}%` : "—"}
            </td>
            <td style={{ textAlign: "right", padding: "8px 10px", fontWeight: 500 }}>
              {fmt(lineTotal(item), currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Totals({ invoice, currency }: { invoice: Invoice; currency: string }) {
  const balance = invoice.total - invoice.totalAmountReceived;
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
      <div style={{ minWidth: 240, fontSize: 13 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ color: "#6b7280" }}>Subtotal</span>
          <span>{fmt(invoice.subTotal, currency)}</span>
        </div>
        {invoice.discountTotal > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: "#6b7280" }}>Discount</span>
            <span style={{ color: "#ef4444" }}>-{fmt(invoice.discountTotal, currency)}</span>
          </div>
        )}
        {invoice.taxRate > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: "#6b7280" }}>Tax ({invoice.taxRate}%)</span>
            <span>{fmt(invoice.taxAmount, currency)}</span>
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: "2px solid #111827",
            paddingTop: 8,
            marginTop: 8,
            fontWeight: 700,
            fontSize: 16,
          }}
        >
          <span>Total</span>
          <span>{fmt(invoice.total, currency)}</span>
        </div>
        {invoice.totalAmountReceived > 0 && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, color: "#10b981" }}>
              <span>Amount Paid</span>
              <span>{fmt(invoice.totalAmountReceived, currency)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 700,
                color: balance > 0 ? "#ef4444" : "#10b981",
                marginTop: 4,
              }}
            >
              <span>Balance Due</span>
              <span>{fmt(Math.max(0, balance), currency)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Template: Classic ────────────────────────────────────────────────────────

function ClassicTemplate({ invoice, business }: InvoiceTemplateProps) {
  const currency = invoice.currency || business.currency || "NGN";
  return (
    <div style={{ fontFamily: "Georgia, serif", background: "#fff", padding: 40, width: "100%", maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
        <div>
          {business.logo && <img src={business.logo} alt="" crossOrigin="anonymous" style={{ maxHeight: 56, marginBottom: 8, display: "block" }} />}
          <div style={{ fontWeight: 700, fontSize: 20 }}>{business.name}</div>
          {business.address && <div style={{ color: "#6b7280", fontSize: 13 }}>{business.address}</div>}
          {business.phone && <div style={{ color: "#6b7280", fontSize: 13 }}>{business.phone}</div>}
          {business.email && <div style={{ color: "#6b7280", fontSize: 13 }}>{business.email}</div>}
          {business.website && <div style={{ color: "#6b7280", fontSize: 13 }}>{business.website}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end", marginBottom: 4 }}>
            <h2 style={{ fontWeight: 700, fontSize: 26, margin: 0 }}>INVOICE</h2>
            <StatusBadge status={invoice.status} />
          </div>
          <div style={{ color: "#6b7280", fontSize: 13 }}>#{invoice.invoiceNumber}</div>
          {invoice.issueDate && (
            <div style={{ color: "#6b7280", fontSize: 12 }}>
              Issued: {new Date(invoice.issueDate).toLocaleDateString()}
            </div>
          )}
          {invoice.dueDate && (
            <div style={{ color: "#ef4444", fontWeight: 600, fontSize: 12 }}>
              Due: {new Date(invoice.dueDate).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 28, gap: 20 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>
            Bill To
          </div>
          <div style={{ fontWeight: 600 }}>{invoice.client.name}</div>
          {invoice.client.email && <div style={{ color: "#6b7280", fontSize: 13 }}>{invoice.client.email}</div>}
          {invoice.client.phone && <div style={{ color: "#6b7280", fontSize: 13 }}>{invoice.client.phone}</div>}
          {invoice.client.address && <div style={{ color: "#6b7280", fontSize: 13 }}>{invoice.client.address}</div>}
        </div>
      </div>
      <ItemsTable invoice={invoice} currency={currency} />
      <Totals invoice={invoice} currency={currency} />
      {invoice.notes && (
        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, marginTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>
            Notes
          </div>
          <div style={{ color: "#374151", fontSize: 13, whiteSpace: "pre-wrap" }}>{invoice.notes}</div>
        </div>
      )}
      {invoice.terms && (
        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, marginTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>
            Terms & Conditions
          </div>
          <div style={{ color: "#374151", fontSize: 13, whiteSpace: "pre-wrap" }}>{invoice.terms}</div>
        </div>
      )}
    </div>
  );
}

// ── Template: Modern ─────────────────────────────────────────────────────────

function ModernTemplate({ invoice, business }: InvoiceTemplateProps) {
  const currency = invoice.currency || business.currency || "NGN";
  return (
    <div style={{ fontFamily: "Inter, sans-serif", background: "#fff", width: "100%", maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
      <div style={{ background: "#111827", color: "#fff", padding: "28px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          {business.logo && <img src={business.logo} alt="" crossOrigin="anonymous" style={{ maxHeight: 40, marginBottom: 6, display: "block" }} />}
          <div style={{ fontWeight: 700, fontSize: 18 }}>{business.name}</div>
          {business.phone && <div style={{ color: "#9ca3af", fontSize: 12 }}>{business.phone}</div>}
          {business.email && <div style={{ color: "#9ca3af", fontSize: 12 }}>{business.email}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 800, fontSize: 28, letterSpacing: -1 }}>INVOICE</div>
          <div style={{ color: "#9ca3af", fontSize: 13 }}>#{invoice.invoiceNumber}</div>
          <StatusBadge status={invoice.status} />
        </div>
      </div>
      <div style={{ padding: "28px 40px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>Bill To</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{invoice.client.name}</div>
            {invoice.client.email && <div style={{ color: "#6b7280", fontSize: 13 }}>{invoice.client.email}</div>}
            {invoice.client.address && <div style={{ color: "#6b7280", fontSize: 13 }}>{invoice.client.address}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            {invoice.issueDate && (
              <div>
                <span style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase" }}>Issue Date: </span>
                <span style={{ fontSize: 13 }}>{new Date(invoice.issueDate).toLocaleDateString()}</span>
              </div>
            )}
            {invoice.dueDate && (
              <div>
                <span style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase" }}>Due Date: </span>
                <span style={{ fontSize: 13, color: "#ef4444", fontWeight: 600 }}>{new Date(invoice.dueDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
        <ItemsTable invoice={invoice} currency={currency} />
        <Totals invoice={invoice} currency={currency} />
        {invoice.notes && (
          <div style={{ background: "#f9fafb", borderRadius: 8, padding: "14px 16px", marginTop: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>Notes</div>
            <div style={{ color: "#374151", fontSize: 13 }}>{invoice.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Template: Minimal ────────────────────────────────────────────────────────

function MinimalTemplate({ invoice, business }: InvoiceTemplateProps) {
  const currency = invoice.currency || business.currency || "NGN";
  return (
    <div style={{ fontFamily: "system-ui, sans-serif", background: "#fff", padding: 48, width: "100%", maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
      <div style={{ marginBottom: 36 }}>
        <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>Invoice</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ fontWeight: 300, fontSize: 32, margin: 0 }}>#{invoice.invoiceNumber}</h2>
          <StatusBadge status={invoice.status} />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 36 }}>
        <div>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>From</div>
          {business.logo && <img src={business.logo} alt="" crossOrigin="anonymous" style={{ maxHeight: 44, marginBottom: 6, display: "block" }} />}
          <div style={{ fontWeight: 600 }}>{business.name}</div>
          {business.address && <div style={{ color: "#6b7280", fontSize: 13 }}>{business.address}</div>}
          {business.phone && <div style={{ color: "#6b7280", fontSize: 13 }}>{business.phone}</div>}
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>To</div>
          <div style={{ fontWeight: 600 }}>{invoice.client.name}</div>
          {invoice.client.email && <div style={{ color: "#6b7280", fontSize: 13 }}>{invoice.client.email}</div>}
          {invoice.client.address && <div style={{ color: "#6b7280", fontSize: 13 }}>{invoice.client.address}</div>}
        </div>
        {invoice.issueDate && (
          <div>
            <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Issue Date</div>
            <div style={{ fontSize: 13 }}>{new Date(invoice.issueDate).toLocaleDateString()}</div>
          </div>
        )}
        {invoice.dueDate && (
          <div>
            <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Due Date</div>
            <div style={{ fontSize: 13, color: "#ef4444", fontWeight: 600 }}>{new Date(invoice.dueDate).toLocaleDateString()}</div>
          </div>
        )}
      </div>
      <ItemsTable invoice={invoice} currency={currency} />
      <Totals invoice={invoice} currency={currency} />
      {invoice.notes && (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #f3f4f6" }}>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Notes</div>
          <div style={{ color: "#6b7280", fontSize: 13 }}>{invoice.notes}</div>
        </div>
      )}
    </div>
  );
}

// ── Template dispatcher ───────────────────────────────────────────────────────

const TEMPLATES: Record<TemplateId, React.FC<InvoiceTemplateProps>> = {
  classic: ClassicTemplate,
  modern: ModernTemplate,
  minimal: MinimalTemplate,
  professional: ClassicTemplate,
  bold: ModernTemplate,
  elegant: MinimalTemplate,
  formal: ClassicTemplate,
  compact: MinimalTemplate,
  corporate: ModernTemplate,
  simple: MinimalTemplate,
};

export const InvoiceTemplate: React.FC<InvoiceTemplateProps> = ({ invoice, business }) => {
  const Template = TEMPLATES[invoice.templateId] || ClassicTemplate;
  return <Template invoice={invoice} business={business} />;
};

export default InvoiceTemplate;
